import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import TouchstreamConfigForm from '../../components/TouchstreamConfigForm';
import {
  getTouchstreamConfigs,
  deleteTouchstreamConfig,
} from '../../api/touchstream';

function TouchstreamConfig() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getTouchstreamConfigs();
      setConfigs(data);
    } catch (error) {
      console.error('Failed to fetch Touchstream configs:', error);
      setError('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedConfig(null);
    setDialogOpen(true);
  };

  const handleEdit = (config) => {
    setSelectedConfig(config);
    setDialogOpen(true);
  };

  const handleDelete = async (configId, configName) => {
    if (window.confirm(`Are you sure you want to delete the configuration "${configName}"?`)) {
      try {
        await deleteTouchstreamConfig(configId);
        fetchConfigs();
      } catch (error) {
        console.error('Failed to delete configuration:', error);
        setError('Failed to delete configuration');
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedConfig(null);
  };

  const handleSuccess = () => {
    setDialogOpen(false);
    setSelectedConfig(null);
    fetchConfigs();
  };

  const maskCredential = (value) => {
    if (!value) return '';
    const visibleChars = 4;
    if (value.length <= visibleChars * 2) {
      return '••••••••';
    }
    return value.substring(0, visibleChars) + '••••' + value.substring(value.length - visibleChars);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Touchstream Configuration</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Add Configuration
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>FQDN</TableCell>
              <TableCell>Context Type</TableCell>
              <TableCell>X-TS-ID</TableCell>
              <TableCell>Bearer Token</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary" py={4}>
                    No Touchstream configurations. Click "Add Configuration" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              configs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{config.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {config.fqdn}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.context_type}
                      size="small"
                      color={config.context_type === 'STATIC' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {maskCredential(config.x_ts_id)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {maskCredential(config.bearer_token)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {config.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(config.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(config)}
                      title="Edit Configuration"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(config.id, config.name)}
                      title="Delete Configuration"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <TouchstreamConfigForm
          config={selectedConfig}
          onClose={handleDialogClose}
          onSuccess={handleSuccess}
        />
      </Dialog>
    </Box>
  );
}

export default TouchstreamConfig;